/**
 * The figures a stats surface prints: durations, counts, bytes.
 *
 * Lives in `lib/` because three surfaces show the same numbers and each had grown its own
 * copy — the session share card (`display/sessionCard`), the summary modal
 * (`display/SessionSummaryModal`) and the profile's Wrapped (`control/profile/ProfileBody`).
 * That is exactly the state `lib/elapsed.ts` already documents the cost of: one copy
 * quietly gains a fix the others do not. The profile's had already drifted — it was the
 * one without a clamp.
 *
 * NOT the live clock. `formatElapsed` in `lib/elapsed.ts` is a RUNNING duration, zero-padded
 * and floored to the second it is on (`8:05`, `1:02:09`), and it belongs to the goon bar
 * and the remote. These are after-the-fact figures in prose units (`47m 12s`). Folding the
 * two together would take the padding to one surface or the seconds to the other.
 *
 * Durations carry their unit IN THE NAME, because the callers disagree: the card and the
 * modal hold milliseconds, the profile holds seconds, and a thousand-fold mistake between
 * them is invisible in the type.
 */

/** `"1.2k"`, `"34k"`, `"2.1m"` — for figures nobody reads digit by digit. */
export function fmtCount(n: number): string {
  if (n < 1000) return String(n);
  if (n < 10_000) return `${(n / 1000).toFixed(1).replace(/\.0$/, "")}k`;
  if (n < 1_000_000) return `${Math.round(n / 1000)}k`;
  return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}m`;
}

/** `"2h 14m"`, `"47m 12s"`, `"9s"` — a duration held in milliseconds. */
export function fmtDurationMs(ms: number): string {
  return spellDuration(Math.max(0, Math.round(ms / 1000)));
}

/** The same figure, for the callers that already hold seconds. */
export function fmtDurationSecs(secs: number): string {
  return spellDuration(Math.max(0, Math.round(secs)));
}

/** Clamped at both doors: a negative duration is a bug upstream, and `-1h -1m` is a worse
 *  way to report it than `0s`. */
function spellDuration(s: number): string {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h) return `${h}h ${m}m`;
  if (m) return `${m}m ${sec}s`;
  return `${sec}s`;
}

/**
 * A gap, for the insight lines: `"45s"`, `"8m"`, `"1h 2m"`.
 *
 * Deliberately coarser than fmtDurationMs, which always prints the seconds it has: "first
 * edge at 8m 0s" reads as a stopwatch reading rather than a fact about the evening, and
 * the trailing zero is the least interesting thing on the card.
 */
export function fmtGapMs(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s}s`;
  const h = Math.floor(s / 3600);
  const m = Math.round((s % 3600) / 60);
  return h ? `${h}h ${m}m` : `${m}m`;
}

/**
 * `"340 MB"`, `"1.2 GB"` — DECIMAL, the unit a bandwidth figure is quoted in.
 *
 * Not `control/lib/format.ts`'s `fmtBytes`, which is binary (1024³) and reads a vault's
 * size on the admin surface. They disagree by 7% at the gigabyte and both are right where
 * they are, so they stay apart on purpose.
 */
export function humanBytes(b: number): string {
  if (b >= 1e9) return `${(b / 1e9).toFixed(1)} GB`;
  if (b >= 1e6) return `${Math.round(b / 1e6)} MB`;
  return `${Math.round(b / 1e3)} KB`;
}
